import { noop } from '@shared';
import { IResponse, IRequest, IBridge, ICallback, IListeners } from './types';

declare const window: Window & {
  WebViewJavascriptBridge: any;
  WVJBCallbacks: any;
};

const { EMPTY_OBJECT, EMPTY_ARRAY, EMPTY_FUNCTION } = noop; // 减少内存回收抖动

function loadIframe() {
  const WVJBIframe = document.createElement('iframe');
  WVJBIframe.style.display = 'none';
  WVJBIframe.src = 'https://__bridge_loaded__';
  document.documentElement.appendChild(WVJBIframe);
  setTimeout(() => {
    document.documentElement.removeChild(WVJBIframe);
  }, 0);
  // 注意(临时方案): 为了解决 iOS WKWebView 的 history.go(-1) 没有 indow.WebViewJavascriptBridge 属性问题, 因为原生拦截不到iframe的src
  setTimeout(() => {
    if (!window.WebViewJavascriptBridge) {
      loadIframe();
    }
  }, 50);
}

/**
 * 启动WebView监听
 * https://github.com/marcuswestin/WebViewJavascriptBridge
 * @param {CallJSBridge} callback
 * @returns {void}
 */
function setupWebViewJavascriptBridge(callback: ICallback) {
  if (window.WebViewJavascriptBridge) {
    callback(window.WebViewJavascriptBridge); // bridge = WebViewJavascriptBridge
    return;
  }

  if (window.WVJBCallbacks) {
    window.WVJBCallbacks.push(callback);
    return;
  }

  window.WVJBCallbacks = [callback];

  loadIframe();
}

class Native {
  mock = !/Leedarson/i.test(navigator.userAgent);

  /**
   * 避免重复注册
   */
  registered = false;

  /**
   * 存放JS监听原生事件的回调函数 { key: [() => {}] }
   * @type {{ [key: string]: EventCallback[] }}
   * */
  listeners: IListeners = {};

  /**
   * 核心: JS调用Native
   * @param {object} [request] - 请求参数
   * @param {number} [timeout] - 超时(毫秒)
   * @returns {Promise<Success>} resolve({code, data, desc}) || reject({code, data, desc})
   */
  private callHandler(request: IRequest, timeout = 0) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const timer = 0;
      try {
        if (timeout > 0) {
          setTimeout(() => {
            const code = 408;
            const desc = 'Request Timeout';
            const response = { code, desc, data: request };
            reject(response); // 超时返回, {code, data, desc}
          }, timeout);
        }
        setupWebViewJavascriptBridge((bridge: IBridge) => {
          // 延迟执行
          bridge.callHandler('JsCallNative', request, (response: IResponse) => {
            clearTimeout(timer);
            resolve(response); // 正常返回, {code, data, desc}
          });
        });
      } catch (error) {
        clearTimeout(timer);
        const code = 400;
        const desc = 'Bad Timeout';
        const response = { code, desc, data: error };
        const duration = Date.now() - start;
        reject(response); // 异常返回, {code, data, desc}
      }
    });
  }

  /**
   * JS调用Native(同步)
   * @param {object} request - 必需,请求参数
   */
  public syncSend(request: IRequest) {
    const key = this.getKeyName(request);
    if (this.mock) {
      return;
    }
    setupWebViewJavascriptBridge((bridge: IBridge) => {
      bridge.callHandler('JsCallNative', request, EMPTY_FUNCTION);
    });
  }

  /**
   * JS调用Native(异步)
   * @param {object} request - 必需,请求参数
   * @param {number} [timeout] - 超时(毫秒)
   * @returns {Promise<Success>} resolve({code, data, desc}) || reject({code, data, desc})
   */
  public send(request: IRequest, timeout?: number) {
    if (!this.mock) {
      return this.callHandler(request, timeout); // 正常返回, {code, data, desc}
    }
    return Promise.reject({
      code: 500,
      message: 'need in leedarson app',
    });
  }

  // 获取模块名称
  private getTypeName(request: IRequest) {
    const { key, event } = request;
    if (key.indexOf('Udp') > -1 || event?.indexOf('Udp') > -1) {
      return 'Udp';
    }
    if (key.indexOf('Tcp') > -1 || event?.indexOf('Tcp') > -1) {
      return `Tcp`;
    }
    if (key.indexOf('Ble') > -1 || event?.indexOf('Ble') > -1) {
      return `Ble`;
    }
    if (key.indexOf('SIGMesh') > -1 || event?.indexOf('SIGMesh') > -1) {
      return `SIGMesh`;
    }
    if (key.indexOf('Storage') > -1 || event?.indexOf('Storage') > -1) {
      return `Storage`;
    }
    if (key.indexOf('Logger') > -1 || event?.indexOf('Logger') > -1) {
      return `Logger`;
    }
    if (key.indexOf('System') > -1 || event?.indexOf('System') > -1) {
      return `System`;
    }
    return 'Other';
  }

  // 获取键名称
  private getKeyName(request: IRequest) {
    const { key } = request;
    if (key === 'JSBridge.addEventListener') {
      return request.event; // 监听事件, `event`字段更有意义
    }
    if (key.indexOf('Udp') > -1 || key.indexOf('Tcp') > -1) {
      const { service, method } = request.message || EMPTY_OBJECT;
      if (service || method) {
        return `${service}.${method}`; // UDP和TCP协议，`service`和`method`字段更有意义
      }
    }
    return key;
  }

  /**
   * JS监听Native事件（用于初始化）
   * @returns {void}
   */
  public register() {
    if (this.mock) {
      return;
    }

    if (this.registered) {
      return;
    }

    this.registered = true;

    const registerHandle = async (
      requestData: IRequest,
      responseCallback: ICallback,
    ) => {
      const { key } = requestData || EMPTY_OBJECT;
      try {
        const callbacks = this.getCallbacks(key);
        if (callbacks.length > 0) {
          const promises = callbacks.map((callback: ICallback) =>
            callback(requestData),
          ); // JS监听回调处理
          const data = await Promise.all(promises);
          // data = data.filter(response => !!response); // 3.25.0 目前原生没有主动调用JS，其实原生可以向JS获取token等
          if (
            Object.prototype.toString.call(responseCallback) ===
            '[object Function]'
          ) {
            const responseData = {
              code: 200,
              data,
              desc: 'Native needs response callback',
            };
            responseCallback(responseData); // JS返回监听回调处理结果给原生
          }
        } else {
          const code = 404;
          const desc = 'Listener callback not found';
          console.log({
            code,
            desc,
          });
        }
      } catch (error) {
        const code = 400;
        const desc = 'Listener callback execute error';
        console.log(code, desc);
      }
    };
    setupWebViewJavascriptBridge((bridge) => {
      bridge.registerHandler(
        'NativeCallJs',
        (requestData: IRequest, responseCallback: ICallback) => {
          // 延迟执行
          // setTimeout(registerHandle, 0, requestData, responseCallback);
          registerHandle(requestData, responseCallback);
        },
      );
    });
  }

  /**
   * 获取指定 key 下的事件监听函数列表
   * @param {string} key - 键
   * @returns {EventCallback[]}
   */
  private getCallbacks(key: string) {
    return this.listeners[key] || EMPTY_ARRAY;
  }

  /**
   * JS监听Native事件（用于实际开发）
   * @param {string} key - 键, request.key, Web提供
   * @param {EventCallback} callback - 回调函数
   * @returns {void}
   */
  public on(key: string, callback: ICallback) {
    if (this.listeners[key] && this.listeners[key].length > 0) {
      this.listeners[key].push(callback);
      return Promise.resolve();
    }
    this.listeners[key] = [callback];
    return this.send({
      key: 'JSBridge.addEventListener',
      event: key,
    });
  }

  /**
   * 移除JS监听Native事件（用于实际开发）
   * @param {string} key - 键, request.key, Web提供
   * @param {EventCallback} [callback] - 回调函数
   * @returns {void}
   */
  public off(key: string, callback: ICallback) {
    if (Object.prototype.toString.call(callback) === '[object Function]') {
      const callbacks = this.getCallbacks(key);
      this.listeners[key] = callbacks.filter((cb) => cb !== callback); // 移除某一个回调
      // 事件为空，移除事件
      if (this.listeners[key]?.length === 0) {
        delete this.listeners[key]; // 移除全部回调
        return this.send({
          key: 'JSBridge.removeEventListener',
          event: key,
        });
      }
      return Promise.resolve();
    }
    delete this.listeners[key]; // 移除全部回调
    return this.send({
      key: 'JSBridge.removeEventListener',
      event: key,
    });
  }

  /**
   * 模拟Native调用JS
   * @param {string} key - 键, request.key, Native提供
   * @param {object} data - 原生请求JS数据
   * @returns {void}
   */
  public trigger(key: string, data: IResponse) {
    this.getCallbacks(key).forEach((callback) => callback(data));
  }
}

export default Native;
